import React from 'react';
import {BrowserRouter as Router, Navigate, Outlet, Route, Routes, useParams} from 'react-router-dom';
import NotFound from "./pages/NotFound/NotFound";
import Login from "./pages/Login/Login";
import Setup from "./pages/Setup/Setup";
import './tailwind.css';

import LinkedInCallback from "./components/specific/LinkedInCallback";
import Profile from "./pages/Profile/Profile";

import AppLayout from "./pages/AppLayout";
import StudentLayout from "./pages/StudentLayout";
import AdminLayout from "./pages/AdminLayout";
import FacultyLayout from "./pages/FacultyLayout";

// Import admin/faculty pages
import AdminDashboard from "./pages/Admin/Dashboard";
import AdminSettings from "./pages/Admin/Settings";

// Import faculty-specific pages
import FacultyDashboard from "./pages/Faculty/Dashboard";
import FacultySettings from "./pages/Faculty/Settings";

// Student Routes
import NetworkingPage from "./pages/Students/Networking";
import MenusPage from "./pages/Students/Menus";
import EventsPage from "./pages/Students/Events";
import Networking from "./pages/Students/Networking";
import CoursesPage from "./pages/Students/Courses";
import Home from "./pages/Students/Home";
import StudentSettings from "./pages/Students/Settings";

import {NavProvider} from "./context/NavContext";
import { useAuth } from "./context/AuthContext";
import { useOrganization } from "./context/OrgContext";
import GeneralSettings from "./pages/Admin/Settings/General";

import LoadingPage from "./pages/Loading";
import AcceptInvite from './components/AcceptInvite';
import Support from './pages/Support/Support';
import WorkflowEntry from "./pages/Faculty/WorkflowEntry";
import SectionDashboard from "./pages/Faculty/sections/SectionDashboard";
import InterfaceDashboard from "./pages/Faculty/interfaces/InterfaceDashboard";


// Loading Component
const LoadingScreen = () => (
  <div className="w-screen h-screen flex justify-center items-center bg-gradient-to-br from-[#0a0a0a] via-[#0f0b08] to-[#0a0a0a]">
    <LoadingPage />
  </div>
);

// Role-based Dashboard Component - Renders different content based on user role
const RoleDashboard = ({ userRole }) => {
  switch (userRole) {
    case 'admin':
      return (
          <AdminLayout>
            <AdminDashboard />
          </AdminLayout>
      );
    case 'faculty':
      return (
          <FacultyLayout>
            <FacultyDashboard />
          </FacultyLayout>
      );
    case 'student':
    default:
      return (
          <NavProvider>
            <StudentLayout>
              <Home />
            </StudentLayout>
          </NavProvider>
      );
  }
};

const RoleSettings = ({ userRole }) => {
  switch (userRole) {
    case 'admin':
      return (
          <AdminLayout>
            <AdminSettings />
          </AdminLayout>
      );
    case 'faculty':
      return (
          <FacultyLayout>
            <FacultySettings />
          </FacultyLayout>
      );
    case 'student':
    default:
      return (
          <NavProvider>
            <StudentLayout>
              <StudentSettings />
            </StudentLayout>
          </NavProvider>
      );
  }
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles, userRole }) => {
  if (!allowedRoles.includes(userRole)) {
    return <Navigate to="/" replace />;
  }
  return children;
};

const AppRoutes = () => {
  const { currentUser } = useAuth();
  const { needsOnboarding, isOrgLoading } = useOrganization();

  const getUserRole = () => {
    if (currentUser?.role === "Admin" || currentUser?.role === "Super Admin") {
      return 'admin';
    }
    if (currentUser?.role === "Faculty" || currentUser?.role === "Staff") {
      return 'faculty';
    }
    return 'student';
  };

  const userRole = getUserRole();

  // Define protected routes that should not be treated as section slugs
  const protectedRoutes = [
    'login', 'setup', 'settings', 'profile', 'menus', 'events',
    'courses', 'networking', 'workflows', 'accept-invite'
  ];

  // Dynamic Route Component - Handles arbitrary nesting levels
  const DynamicNestedRoute = ({ protectedRoutes }) => {
    const params = useParams();
    const pathSegments = window.location.pathname.split('/').filter(Boolean);
    
    // Check if the first segment is a protected route
    if (pathSegments.length > 0 && protectedRoutes.includes(pathSegments[0])) {
      return <Navigate to="/" replace />;
    }
    
    // Determine what type of component to render based on path structure
    if (pathSegments.length === 1) {
      // Single segment: render section dashboard
      return <SectionDashboard />;
    } else if (pathSegments.length === 2) {
      // Two segments: render interface dashboard
      return <InterfaceDashboard />;
    } else if (pathSegments.length >= 3) {
      // Three or more segments: determine if it's a workflow or nested section/interface
      const lastSegment = pathSegments[pathSegments.length - 1];
      const secondLastSegment = pathSegments[pathSegments.length - 2];
      
      if (secondLastSegment === 'workflows') {
        // Pattern: .../workflows/{workflowId}
        return <WorkflowEntry />;
      } else {
        // Pattern: nested section or interface
        // For odd-numbered segments (3, 5, 7...), render as section dashboard
        // For even-numbered segments (4, 6, 8...), render as interface dashboard
        const isSection = pathSegments.length % 2 === 1;
        return isSection ? <SectionDashboard isSubsection={true} /> : <InterfaceDashboard />;
      }
    }
    
    return <Navigate to="/" replace />;
  };

  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/setup" element={<Setup />} />
        <Route path="/support" element={<Support />} />
        <Route path="/networking/linkedin-callback" element={<LinkedInCallback />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />

        {/* Protected Routes with Role-Based Access */}
        <Route
          path="*"
          element={
            // Show loading while checking organization
            isOrgLoading ? (
              <LoadingScreen />
            ) : (
              // Redirect to setup if onboarding needed and user is authenticated
              currentUser && needsOnboarding ? (
                <Navigate to="/setup" replace />
              ) : (
                <AppLayout>
                  <Routes>
                    {/* Dynamic Home Route - Renders Different Content Based on Role */}
                    <Route path="/" element={<RoleDashboard userRole={userRole} />} />

                    <Route path="/settings" element={<RoleSettings userRole={userRole} />}>
                      {/* Nested settings for admin */}
                      {userRole === "admin" && (
                        <>
                          <Route index element={<GeneralSettings />} />
                          <Route path="users" element={<div>Admin Users</div>} />
                          <Route path="billing" element={<div>Admin Billing</div>} />
                          <Route path="profile" element={<div>Admin Profile</div>} />
                        </>
                      )}

                      {/* Nested settings for faculty */}
                      {userRole === "faculty" && (
                        <>
                          <Route index element={<div>Faculty General Settings</div>} />
                          <Route path="profile" element={<div>Faculty Profile Settings</div>} />
                        </>
                      )}

                      {/* Nested settings for student */}
                      {userRole === "student" && (
                        <>
                          <Route index element={<StudentSettings />} />
                          <Route path="profile" element={<div>Student Profile Settings</div>} />
                        </>
                      )}
                    </Route>

                    {/* Root-level workflow routes */}
                    <Route path="/workflows/:workflowSlug" element={<WorkflowEntry />} />

                    {/* Dynamic nested routes - handles arbitrary nesting */}
                    <Route path="/*" element={<DynamicNestedRoute protectedRoutes={protectedRoutes} />} />

                    <Route path="/profile" element={
                      <NavProvider>
                        <StudentLayout>
                          <Profile />
                        </StudentLayout>
                      </NavProvider>
                    } />

                    <Route path="/menus" element={<Outlet />}>
                      {userRole === "student" && (
                         <>
                          <Route index element={
                              <>
                                  <NavProvider>
                                    <StudentLayout>
                                      <MenusPage />
                                    </StudentLayout>
                                  </NavProvider>
                              </>
                          } />
                        </>
                      )}
                    </Route>

                    <Route path="/events" element={<Outlet />}>
                      {userRole === "student" && (
                         <>
                          <Route index element={
                              <>
                                  <NavProvider>
                                    <StudentLayout>
                                      <EventsPage />
                                    </StudentLayout>
                                  </NavProvider>
                              </>
                          } />
                        </>
                      )}
                    </Route>

                    <Route path="/courses" element={
                      <NavProvider>
                        <StudentLayout>
                          <CoursesPage />
                        </StudentLayout>
                      </NavProvider>
                    } />

                    <Route path="/networking" element={
                      <NavProvider>
                        <StudentLayout>
                          <NetworkingPage />
                        </StudentLayout>
                      </NavProvider>
                    } />


                    {/* Fallback for unmatched routes */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </AppLayout>
              )
            )
          }
        />

        {/* Global Fallback */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
};

export default AppRoutes;